/**
 * API Hooks
 *
 * Custom React Query hooks for all API endpoints.
 * Provides type-safe, cache-managed data fetching and mutations.
 */

import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { apiClient } from "./client";
import {
  SignupLinkRequest,
  LoginLinkRequest,
  MagicLinkResponse,
  Listing,
  ListingCreateRequest,
  Message,
  MessageCreateRequest,
  CheckoutSessionRequest,
  CheckoutSession,
  PaginatedListings,
  ListingsFilter,
} from "./schemas";

/**
 * Query keys for React Query
 * Organized by domain to enable selective cache invalidation
 */
export const queryKeys = {
  auth: {
    all: ["auth"] as const,
    account: () => [...queryKeys.auth.all, "account"] as const,
  },
  listings: {
    all: ["listings"] as const,
    search: (filters: ListingsFilter) => [...queryKeys.listings.all, "search", filters] as const,
    detail: (id: string) => [...queryKeys.listings.all, "detail", id] as const,
    myListings: () => [...queryKeys.listings.all, "my-listings"] as const,
  },
  messages: {
    all: ["messages"] as const,
    threads: (listingId: string) => [...queryKeys.messages.all, "threads", listingId] as const,
    thread: (threadId: string) => [...queryKeys.messages.all, "thread", threadId] as const,
  },
  admin: {
    all: ["admin"] as const,
    listings: () => [...queryKeys.admin.all, "listings"] as const,
    scraped: () => [...queryKeys.admin.all, "scraped"] as const,
  },
};

// ============================================================================
// Auth Hooks
// ============================================================================

export function useSignupLink(options?: UseMutationOptions<void, Error, SignupLinkRequest>) {
  return useMutation({
    mutationFn: (req) => apiClient.post("/v1/auth/signup-link", req),
    ...options,
  });
}

export function useLoginLink(options?: UseMutationOptions<void, Error, LoginLinkRequest>) {
  return useMutation({
    mutationFn: (req) => apiClient.post("/v1/auth/login-link", req),
    ...options,
  });
}

export function useRefreshToken(options?: UseMutationOptions<MagicLinkResponse, Error, void>) {
  return useMutation({
    mutationFn: async () => {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) throw new Error("No refresh token available");
      return apiClient.post<MagicLinkResponse>("/v1/auth/refresh", { refreshToken });
    },
    ...options,
  });
}

// ============================================================================
// Listing Hooks (Buyer/Public)
// ============================================================================

export function useListing(id: string, options?: UseQueryOptions<Listing>) {
  return useQuery({
    queryKey: queryKeys.listings.detail(id),
    queryFn: async () => {
      try {
        return await apiClient.get<Listing>(`/v1/listings/${id}`);
      } catch (error) {
        // Fallback to static JSON file on API failure
        console.warn(`Failed to fetch listing ${id} from API, trying static data:`, error);
        return fetchStaticListingDetail(id);
      }
    },
    ...options,
  });
}

/**
 * Fetch a single listing from static JSON file by ID
 */
async function fetchStaticListingDetail(id: string): Promise<Listing> {
  const response = await fetch("/data/boat-listings.json");
  if (!response.ok) {
    throw new Error("Failed to load static listings");
  }

  const data = await response.json();
  const listing = data.listings.find((l: any) => l.id === id);

  if (!listing) {
    throw new Error(`Listing with ID ${id} not found`);
  }

  return listing as Listing;
}

export function useSearchListings(
  filters: ListingsFilter,
  options?: UseQueryOptions<PaginatedListings>
) {
  return useQuery({
    queryKey: queryKeys.listings.search(filters),
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filters.search) params.append("search", filters.search);
        if (filters.type?.length) params.append("type", filters.type.join(","));
        if (filters.minPrice) params.append("minPrice", filters.minPrice.toString());
        if (filters.maxPrice) params.append("maxPrice", filters.maxPrice.toString());
        if (filters.location) params.append("location", filters.location);
        if (filters.cursor) params.append("cursor", filters.cursor);
        params.append("limit", filters.limit.toString());

        return await apiClient.get<PaginatedListings>(`/v1/listings?${params.toString()}`);
      } catch (error) {
        // Fallback to static JSON file on API failure
        console.warn("API call failed, falling back to static data:", error);
        return fetchStaticListings(filters);
      }
    },
    ...options,
  });
}

/**
 * Fetch listings from static JSON file
 * Used as fallback when API is unavailable
 */
async function fetchStaticListings(filters: ListingsFilter): Promise<PaginatedListings> {
  const response = await fetch("/data/boat-listings.json");
  if (!response.ok) {
    throw new Error("Failed to load static listings");
  }

  const data = await response.json();
  let listings = data.listings as Listing[];

  // Apply filters
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    listings = listings.filter(
      (l) =>
        l.title.toLowerCase().includes(searchLower) ||
        l.description.toLowerCase().includes(searchLower) ||
        l.make.toLowerCase().includes(searchLower) ||
        l.model.toLowerCase().includes(searchLower)
    );
  }

  if (filters.type?.length) {
    listings = listings.filter((l) => filters.type!.includes(l.type));
  }

  if (filters.minPrice) {
    listings = listings.filter((l) => l.price >= filters.minPrice!);
  }

  if (filters.maxPrice) {
    listings = listings.filter((l) => l.price <= filters.maxPrice!);
  }

  if (filters.location) {
    const locationLower = filters.location.toLowerCase();
    listings = listings.filter((l) => l.location.toLowerCase().includes(locationLower));
  }

  if (filters.minLength) {
    listings = listings.filter((l) => l.length >= filters.minLength!);
  }

  if (filters.maxLength) {
    listings = listings.filter((l) => l.length <= filters.maxLength!);
  }

  // Apply pagination
  const limit = filters.limit || 20;
  const offset = 0; // Simple pagination for static data
  const paginatedListings = listings.slice(offset, offset + limit);

  return {
    listings: paginatedListings,
    hasMore: listings.length > offset + limit,
    nextCursor: listings.length > offset + limit ? `offset_${offset + limit}` : null,
  };
}

export function useCreateListing(options?: UseMutationOptions<Listing, Error, ListingCreateRequest>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req) => apiClient.post("/v1/listings", req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listings.myListings() });
    },
    ...options,
  });
}

export function useMyListings(options?: UseQueryOptions<{ listings: Listing[] }>) {
  return useQuery({
    queryKey: queryKeys.listings.myListings(),
    queryFn: () => apiClient.get<{ listings: Listing[] }>("/v1/listings/me"),
    ...options,
  });
}

export function useUpdateListing(
  listingId: string,
  options?: UseMutationOptions<Listing, Error, Partial<ListingCreateRequest>>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req) => apiClient.patch(`/v1/listings/${listingId}`, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listings.myListings() });
      queryClient.invalidateQueries({ queryKey: queryKeys.listings.detail(listingId) });
    },
    ...options,
  });
}

export function useDeleteListing(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (listingId) => apiClient.delete(`/v1/listings/${listingId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.listings.myListings() });
    },
    ...options,
  });
}

// ============================================================================
// Messaging Hooks
// ============================================================================

export function useCreateMessage(
  options?: UseMutationOptions<Message, Error, MessageCreateRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req) => apiClient.post("/v1/messages", req),
    onSuccess: (data) => {
      // Invalidate threads for the listing
      queryClient.invalidateQueries({
        queryKey: queryKeys.messages.threads(data.listingId),
      });
    },
    ...options,
  });
}

export function useListingMessages(
  listingId: string,
  options?: UseQueryOptions<{ messages: Message[]; nextCursor: string | null }>
) {
  return useQuery({
    queryKey: queryKeys.messages.threads(listingId),
    queryFn: () =>
      apiClient.get<{ messages: Message[]; nextCursor: string | null }>(
        `/v1/messages?listingId=${listingId}`
      ),
    ...options,
  });
}

export function useSellerMessages(
  options?: UseQueryOptions<{ threads: Message[] }>
) {
  return useQuery({
    queryKey: queryKeys.messages.all,
    queryFn: () =>
      apiClient.get<{ threads: Message[] }>("/v1/messages/seller/threads"),
    ...options,
  });
}

export function useMarkMessageRead(options?: UseMutationOptions<void, Error, string>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId) => apiClient.patch(`/v1/messages/${messageId}/read`),
    onSuccess: () => {
      // Invalidate all message threads
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
    ...options,
  });
}

// ============================================================================
// Billing Hooks
// ============================================================================

export function useCreateCheckoutSession(
  options?: UseMutationOptions<CheckoutSession, Error, CheckoutSessionRequest>
) {
  return useMutation({
    mutationFn: (req) => apiClient.post("/v1/billing/checkout", req),
    ...options,
  });
}

// ============================================================================
// Admin Hooks
// ============================================================================

export function useAdminListings(
  options?: UseQueryOptions<{ listings: Listing[] }>
) {
  return useQuery({
    queryKey: queryKeys.admin.listings(),
    queryFn: () =>
      apiClient.get<{ listings: Listing[] }>("/v1/admin/listings"),
    ...options,
  });
}

export function useUpdateListingStatus(
  listingId: string,
  options?: UseMutationOptions<void, Error, { status: string; rejectionReason?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) =>
      apiClient.patch(`/v1/admin/listings/${listingId}/status`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.listings() });
    },
    ...options,
  });
}

// ============================================================================
// Scraping/Admin Hooks (future expansion)
// ============================================================================

export function useScrapedListings(
  options?: UseQueryOptions<{ listings: Listing[] }>
) {
  return useQuery({
    queryKey: queryKeys.admin.scraped(),
    queryFn: () =>
      apiClient.get<{ listings: Listing[] }>("/v1/admin/scraped-listings"),
    ...options,
  });
}
